import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import { IconButton, Box as MuiBox, TextField, Typography } from '@mui/material'
import { MainApi } from 'api/MainApi'
import { type GausiumExecutableTaskRecent, RobotApi } from 'api/RobotApi'
import { OdysseyRead } from 'api/schema'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import FavoriteStar from 'components/FavoriteStar/FavoriteStar'
import FloorMapPreviewOverlay from 'components/FloorMapPreviewOverlay'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import MissionStats from 'components/MissionStats/MissionStats'
import NormalizedMissionHistoryPanel from 'components/NormalizedMissionHistoryPanel/NormalizedMissionHistoryPanel'
import ScenarioTable from 'components/ScenarioTable/ScenarioTable'
import StationsOnMap from 'components/StationsOnMap/StationsOnMap'
import MaintenancePointsOnMap from 'components/MaintenancePointsOnMap/MaintenancePointsOnMap'
import AdaptiveTabs from 'components/AdaptiveTabs/AdaptiveTabs'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TablePagination from 'components/TablePagination/TablePagination'
import Title from 'components/Title/Title'
import { Feature, Point } from 'geojson'
import { useIsMobile } from 'hooks/useIsMobile'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useUser } from 'hooks/user'
import { ReactComponent as GearSvg } from 'images/icons/gear.svg'
import { getFacilityVendorUiLayout } from 'pages/Facility/FacilityView/facilityVendorLayout'
import AddFacilityModal from 'pages/FacilityList/AddFacilityModal/AddFacilityModal'
import AssetsView from 'pages/Assets/AssetsView'
import { AssetsTab, parseAssetsTab } from 'pages/Assets/assetsTab'
import AddMaintenancePointModal from 'pages/Assets/maintenance/AddMaintenancePointModal/AddMaintenancePointModal'
import CleaningPointsOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import CleaningZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesOnMap'
import MissionStartFinishPoints from 'pages/Mission/MissionView/MissionMap/Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/TransferRoutesOnMap'
import SelectMissionModal from 'pages/Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectMissionModal/SelectMissionModal'
import AllRobotsOnMap from 'pages/Assets/robots/AllRobotsOnMap/AllRobotsOnMap'
import AllStationsOnMap from 'pages/Assets/stations/AllStationsOnMap/AllStationsOnMap'
import { ConfirmationDialogAllSingle } from 'pages/TaskScheduler/ConfirmationDialogAllSingle'
import { UTC_OFFSET_OPTIONS, formatUtcOffset, toISOStringYYYYMMDD } from 'pages/TaskScheduler/TaskSchedulerUtils'
import type { DialogTarget, PickerScope } from 'pages/TaskScheduler/TaskSettingsModal/DialogContext'
import TaskSettingsModal from 'pages/TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { useNavigate } from 'react-router-dom'
import { ROBOT_MODES, ROBOT_NETWORK_STATUSES, TASK_STATUSES } from 'utils/constants'
import { GAUSIUM_ENABLED, PROGRESS_TYPES } from 'utils/constants'
import { hasRecurringWeekdays } from 'utils/odysseyReschedule'
import { TASK_CANCEL_REASON_OPTIONS, TASK_CANCEL_REASON_OTHER } from 'utils/taskCancelReasons'
import { IMapboxElement, ILot, IMaintenancePoint, IMission, IMissionInfo, IRobotInfo, IStation } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'
import { SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX, SCENARIO_MAP_COLUMN_INSET_LEFT_PX } from 'utils/scenarioMapLayout'
import { applyStateAction, enumParamUpper } from 'utils/urlQueryState'
import { VENDOR, type VendorCode, getVendorDisplayName } from 'utils/vendorDisplayName'

import FacilityCleaningTaskItem, {
  SCHEDULE_PLACEHOLDER_TEXT,
} from './FacilityCleaningTaskItem/FacilityCleaningTaskItem'
import GausiumMapGeoPinsOnMap, { GausiumMapGeoPin } from './GausiumMapGeoPinsOnMap/GausiumMapGeoPinsOnMap'
import GausiumScenariosTabContent from './GausiumScenariosTabContent/GausiumScenariosTabContent'

import style from './FacilityView.module.css'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'

const PAGE_SIZE = 20
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000

/** Vendors selectable on Cleaning / Scenarios / History tabs (Asbis is fleet-only on Facility). */
const FACILITY_TASK_VENDOR_OPTIONS: IOption[] = [
  { value: VENDOR.AUTONOMICS, text: getVendorDisplayName(VENDOR.AUTONOMICS) },
  { value: VENDOR.GAUSIUM, text: getVendorDisplayName(VENDOR.GAUSIUM) },
]

function facilityTaskVendorCodes(codes: Set<VendorCode>): Set<VendorCode> {
  const result = new Set<VendorCode>()
  if (codes.has(VENDOR.AUTONOMICS)) result.add(VENDOR.AUTONOMICS)
  if (codes.has(VENDOR.GAUSIUM)) result.add(VENDOR.GAUSIUM)
  return result
}

function pickDefaultFacilityVendor(codes: Set<VendorCode>, robots: IRobotInfo[]): VendorCode {
  const taskCodes = facilityTaskVendorCodes(codes)
  if (taskCodes.size === 0) return VENDOR.AUTONOMICS
  if (taskCodes.size === 1) return taskCodes.values().next().value as VendorCode
  for (const r of robots) {
    const v = r.vendor as VendorCode
    if (taskCodes.has(v)) return v
  }
  for (const opt of FACILITY_TASK_VENDOR_OPTIONS) {
    if (taskCodes.has(String(opt.value) as VendorCode)) return String(opt.value) as VendorCode
  }
  return VENDOR.AUTONOMICS
}

/** Logical tab indices for content panels (stable when MUI hides vendor-specific tabs). */
const FACILITY_TAB = {
  CLEANING: 0,
  FLEET: 1,
  SCENARIOS: 2,
  HISTORY: 3,
} as const

function maxFacilityTab(showScenariosTab: boolean, showHistoryTab: boolean): number {
  if (showHistoryTab) return FACILITY_TAB.HISTORY
  if (showScenariosTab) return FACILITY_TAB.SCENARIOS
  return FACILITY_TAB.FLEET
}

function facilityTabToMuiValue(activeTab: number, showScenariosTab: boolean, showHistoryTab: boolean): number {
  if (activeTab <= FACILITY_TAB.FLEET) return activeTab
  if (activeTab === FACILITY_TAB.SCENARIOS) return showScenariosTab ? 2 : FACILITY_TAB.FLEET
  if (activeTab === FACILITY_TAB.HISTORY) {
    if (showHistoryTab && showScenariosTab) return 3
    if (showHistoryTab) return 2
    return FACILITY_TAB.FLEET
  }
  return FACILITY_TAB.CLEANING
}

function muiValueToFacilityTab(muiValue: number, showScenariosTab: boolean, showHistoryTab: boolean): number {
  if (muiValue <= FACILITY_TAB.FLEET) return muiValue
  if (muiValue === 2) {
    return showScenariosTab ? FACILITY_TAB.SCENARIOS : showHistoryTab ? FACILITY_TAB.HISTORY : FACILITY_TAB.FLEET
  }
  if (muiValue === 3) return showHistoryTab ? FACILITY_TAB.HISTORY : FACILITY_TAB.FLEET
  return FACILITY_TAB.CLEANING
}

function parseNumericId(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value.trim())
  return null
}
// Global promise cache to deduplicate fetches across components and remounts
const missionPromises: Record<number, Promise<IMission>> = {}
const FacilityView = ({
  facility,
  robots,
  stations,
  lots,
  maintenancePoints,
  onRobotsUpdated,
  onStationsChanged,
  onLiveStationFields,
  onMaintenancePointsChanged,
  activeTab,
  setActiveTab,
}: {
  facility: any
  robots: IRobotInfo[]
  stations: IStation[]
  lots: ILot[]
  maintenancePoints: IMaintenancePoint[]
  onRobotsUpdated: () => void
  onStationsChanged: () => void
  onLiveStationFields?: (fieldsById: Record<number, Partial<IStation>>) => void
  onMaintenancePointsChanged: () => void
  activeTab: number
  setActiveTab: (tab: number) => void
}) => {
  const { t } = useTranslation()
  const user = useUser()
  const isMobile = useIsMobile()
  const [showSidebar, setShowSidebar] = useState(true)
  const [highlightedRobotId, setHighlightedRobotId] = useState<number | undefined>(undefined)
  const [highlightedStationId, setHighlightedStationId] = useState<number | undefined>(undefined)
  const [highlightedMaintenancePointId, setHighlightedMaintenancePointId] = useState<number | undefined>(undefined)
  const [url, setUrl] = useUrlQueryState({
    assetsTab: {
      parse: parseAssetsTab,
      serialize: (tab: AssetsTab) => (tab === 'robots' ? undefined : tab),
    },
    vendor: enumParamUpper(['AUTONOMICS', 'GAUSIUM', 'ASBIS'] as const, VENDOR.AUTONOMICS),
  })
  const assetsSubTab = url.assetsTab
  const setAssetsSubTab = (tab: AssetsTab) => setUrl({ assetsTab: tab })
  const [highlightedGausiumExecId, setHighlightedGausiumExecId] = useState<number | undefined>(undefined)
  const [highlightedMissionIds, setHighlightedMissionIds] = useState<number[]>([])
  const [isSelectScenarioModalOpen, setIsSelectScenarioModalOpen] = useState(false)
  const [addingNewMaintenancePoint, setAddingNewMaintenancePoint] = useState(false)
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const [displayOffsetMinutes, setDisplayOffsetMinutes] = useState<number>(() => -new Date().getTimezoneOffset())
  const [isEditFacilityModalOpen, setIsEditFacilityModalOpen] = useState(false)
  const prevFacilityIdRef = useRef<number | undefined>()

  const facilityVendorCodes = useMemo(() => {
    const codes = new Set<VendorCode>()
    for (const r of robots ?? []) {
      if (r.vendor === VENDOR.AUTONOMICS || r.vendor === VENDOR.GAUSIUM || r.vendor === VENDOR.ASBIS) {
        codes.add(r.vendor as VendorCode)
      }
    }
    return codes
  }, [robots])

  const hasGausiumRobots = facilityVendorCodes.has(VENDOR.GAUSIUM)
  const hasAutonomicsRobots = facilityVendorCodes.has(VENDOR.AUTONOMICS)
  const isMixedVendorFacility = facilityVendorCodes.size > 1
  const facilityScenariosHistoryVendor = url.vendor
  const setFacilityScenariosHistoryVendor = (vendor: React.SetStateAction<VendorCode>) =>
    setUrl(s => ({ vendor: applyStateAction(s.vendor, vendor) }))

  const historyRobots = useMemo((): IRobotInfo[] => {
    if (!isMixedVendorFacility) return robots
    if (facilityScenariosHistoryVendor === VENDOR.GAUSIUM) {
      return robots.filter(r => r.vendor === VENDOR.GAUSIUM)
    }
    return robots.filter(r => r.vendor === VENDOR.AUTONOMICS)
  }, [robots, isMixedVendorFacility, facilityScenariosHistoryVendor])

  const gausiumRobotIds = useMemo(
    () => (robots ?? []).filter(r => r.vendor === VENDOR.GAUSIUM).map(r => r.id),
    [robots]
  )

  /**
   * Текущий вендор для UI: mixed → dropdown; solo → фактический вендор площадки.
   * Далее весь vendor-specific UI берётся из getFacilityVendorUiLayout (см. facilityVendorLayout.ts).
   */
  const effectiveFacilityVendor = useMemo((): VendorCode => {
    if (isMixedVendorFacility) return facilityScenariosHistoryVendor
    if (facilityVendorCodes.has(VENDOR.ASBIS)) return VENDOR.ASBIS
    if (hasGausiumRobots && !hasAutonomicsRobots) return VENDOR.GAUSIUM
    return VENDOR.AUTONOMICS
  }, [
    isMixedVendorFacility,
    facilityScenariosHistoryVendor,
    facilityVendorCodes,
    hasGausiumRobots,
    hasAutonomicsRobots,
  ])

  const vendorUiLayout = useMemo(
    () => getFacilityVendorUiLayout(effectiveFacilityVendor, { isMixedVendorFacility }),
    [effectiveFacilityVendor, isMixedVendorFacility]
  )

  const facilityVendorDropdownOptions = useMemo(() => {
    const taskCodes = facilityTaskVendorCodes(facilityVendorCodes)
    return FACILITY_TASK_VENDOR_OPTIONS.filter(opt => taskCodes.has(String(opt.value) as VendorCode))
  }, [facilityVendorCodes])

  const muiTabValue = useMemo(
    () => facilityTabToMuiValue(activeTab, vendorUiLayout.tabs.showScenariosTab, vendorUiLayout.tabs.showHistoryTab),
    [activeTab, vendorUiLayout.tabs.showScenariosTab, vendorUiLayout.tabs.showHistoryTab]
  )

  useEffect(() => {
    const maxTab = maxFacilityTab(vendorUiLayout.tabs.showScenariosTab, vendorUiLayout.tabs.showHistoryTab)
    if (activeTab > maxTab) {
      setActiveTab(maxTab)
    }
  }, [vendorUiLayout.tabs.showScenariosTab, vendorUiLayout.tabs.showHistoryTab, activeTab, setActiveTab])

  useEffect(() => {
    if (!facility?.id) return
    if (facilityVendorCodes.size === 0) return

    const facilityChanged = prevFacilityIdRef.current !== facility.id
    if (facilityChanged) {
      prevFacilityIdRef.current = facility.id
      setFacilityScenariosHistoryVendor(pickDefaultFacilityVendor(facilityVendorCodes, robots))
      return
    }

    setFacilityScenariosHistoryVendor(prev => {
      const taskCodes = facilityTaskVendorCodes(facilityVendorCodes)
      if (prev === VENDOR.ASBIS || !taskCodes.has(prev)) {
        return pickDefaultFacilityVendor(facilityVendorCodes, robots)
      }
      // Same vendor — return prev so serialize is a no-op (avoids fighting tab URL writes).
      return prev
    })
  }, [facility?.id, facilityVendorCodes, robots])

  const historyRobotSerialNumbers = useMemo(
    () => Array.from(new Set(historyRobots.map(r => String(r.serialNumber || '').trim()).filter(Boolean))),
    [historyRobots]
  )

  const [openCancelDialog, setOpenCancelDialog] = useState(false)
  const [taskToCancel, setTaskToCancel] = useState<any | null>(null)
  const [deleteReason, setDeleteReason] = useState<string>('')
  const [deleteDescription, setDeleteDescription] = useState('')

  const [selectedMissionsDetails, setSelectedMissionsDetails] = useState<IMission[]>([])
  /** Autonomics: Odyssey IDs hidden from map on Cleaning tab (empty = all visible). */
  const [hiddenScheduledTaskIds, setHiddenScheduledTaskIds] = useState<Set<number>>(new Set())
  /** GS executable task ids hidden from map on Cleaning tab (empty = all visible; pin per map if any row for that map is on). */
  const [hiddenGausiumExecTaskIds, setHiddenGausiumExecTaskIds] = useState<Set<number>>(new Set())
  const [scheduledMissionsDetails, setScheduledMissionsDetails] = useState<IMission[]>([])
  const [referencedScenarios, setReferencedScenarios] = useState<any[]>([])
  const missionCacheRef = useRef<Record<number, IMission>>({})
  const [cacheVersion, setCacheVersion] = useState(0)

  const lastZoomedMissionIds = useRef<string>('')
  const hasZoomedAllCleaning = useRef(false)
  const hasZoomedGausiumCleaningMaps = useRef(false)
  const lastGausiumCameraFocusScenarioId = useRef<number | undefined>(undefined)

  useEffect(() => {
    setHighlightedMissionIds([])
    setHighlightedRobotId(undefined)
    setSelectedMissionsDetails([])
    lastZoomedMissionIds.current = ''
    hasZoomedAllCleaning.current = false
    hasZoomedGausiumCleaningMaps.current = false
    lastGausiumCameraFocusScenarioId.current = undefined
  }, [activeTab])

  useEffect(() => {
    const fetchMissionDetails = async () => {
      if ((activeTab === 0 || activeTab === 2 || activeTab === 3) && highlightedMissionIds.length > 0) {
        try {
          const promises = highlightedMissionIds.map(id => {
            if (missionCacheRef.current[id]) {
              return Promise.resolve(missionCacheRef.current[id])
            }
            if (!missionPromises[id]) {
              missionPromises[id] = MainApi.fetchMission(id)
            }
            return missionPromises[id]
          })
          const results = await Promise.all(promises)

          let updated = false
          results.forEach(m => {
            if (!missionCacheRef.current[m.mission.id]) {
              missionCacheRef.current[m.mission.id] = m
              updated = true
            }
          })
          if (updated) setCacheVersion(v => v + 1)

          setSelectedMissionsDetails(results)
        } catch (error) {
          console.error('Failed to fetch selected missions', error)
        }
      } else {
        setSelectedMissionsDetails([])
      }
    }
    fetchMissionDetails()
  }, [highlightedMissionIds, activeTab])

  useEffect(() => {
    if (map && selectedMissionsDetails.length > 0 && (activeTab === 0 || activeTab === 2 || activeTab === 3)) {
      const currentIdsStr = selectedMissionsDetails
        .map(m => m.mission.id)
        .sort()
        .join(',')
      if (lastZoomedMissionIds.current !== currentIdsStr) {
        let features: Feature[] = []
        if (activeTab === 0) {
          features = selectedMissionsDetails.flatMap(m => m.cleaningRoutes)
        } else {
          features = selectedMissionsDetails.flatMap(m => [
            ...m.cleaningZones,
            ...m.restrictZones,
            ...m.cleaningRoutes,
            ...m.transferRoutes,
            ...(m.pointRoutes || []),
          ])
        }

        if (features.length > 0) {
          zoomMapTo(map, features, isMobile ? 0 : 580)
          lastZoomedMissionIds.current = currentIdsStr
        }
      }
    } else if (map && activeTab === 0 && scheduledMissionsDetails.length > 0 && !hasZoomedAllCleaning.current) {
      const features = scheduledMissionsDetails.flatMap(m => [...m.cleaningZones, ...m.cleaningRoutes])
      if (features.length > 0) {
        zoomMapTo(map, features, isMobile ? 0 : 580)
        hasZoomedAllCleaning.current = true
      }
    }
  }, [map, selectedMissionsDetails, scheduledMissionsDetails, activeTab])

  /** Cleaning tab: gate scheduled-tasks block (no network fetch; catalog moved to History tab). */
  const [missionsProgress, setMissionsProgress] = useState(PROGRESS_TYPES.SUCCESS)
  const odysseys: OdysseyRead[] = useMemo(() => robots.flatMap(r => (r.robotRaw as any).odysseys || []), [robots])

  // Cleaning tasks calculation
  const scheduledTasks = useMemo(() => {
    const tasks: (OdysseyRead & {
      name?: string
      missions: number[]
      overall_length?: number
      overall_cleaned_area?: number
    })[] = []
    odysseys.forEach(odyssey => {
      if (odyssey.job_type === 255) return
      if (hasRecurringWeekdays(odyssey.scheduler_settings?.weekdays)) {
        let displayName = odyssey.name || undefined
        let missionIds: number[] = odyssey.missions || []

        let overallLength = 0
        let overallArea = 0

        if (odyssey.scenario) {
          const matchedScenario = referencedScenarios.find(s => s.id === odyssey.scenario)
          if (matchedScenario) {
            displayName = matchedScenario.name || undefined
            missionIds = matchedScenario.missions_ids || []
            overallLength = matchedScenario.overall_length || 0
            overallArea = matchedScenario.overall_cleaned_area || 0
          }
        }

        tasks.push({
          ...odyssey,
          name: displayName,
          missions: missionIds,
          overall_length: overallLength,
          overall_cleaned_area: overallArea,
        })
      }
    })
    tasks.sort((a, b) => {
      const getTime = (t: OdysseyRead) => {
        const datetime = t.scheduler_settings?.datetime
        if (!datetime) return Infinity
        const isoStr = datetime.replace(' ', 'T')
        const timeMatch = isoStr.match(/T(\d{2}):(\d{2})/)
        if (timeMatch) return parseInt(timeMatch[1], 10) * 60 + parseInt(timeMatch[2], 10)
        const dt = new Date(isoStr)
        const time = dt.getHours() * 60 + dt.getMinutes()
        return isNaN(time) ? Infinity : time
      }
      return getTime(a) - getTime(b)
    })

    const overlaps = new Set<number>()
    const robotTasks: Record<number, any[]> = {}
    tasks.forEach(t => {
      if (t.robot_id) {
        if (!robotTasks[t.robot_id]) robotTasks[t.robot_id] = []
        robotTasks[t.robot_id].push(t)
      }
    })

    const getTimeInMinutes = (datetime?: string | null) => {
      if (!datetime) return Infinity
      const isoStr = datetime.replace(' ', 'T')
      const timeMatch = isoStr.match(/T(\d{2}):(\d{2})/)
      if (timeMatch) return parseInt(timeMatch[1], 10) * 60 + parseInt(timeMatch[2], 10)
      const dt = new Date(isoStr)
      const time = dt.getHours() * 60 + dt.getMinutes()
      return isNaN(time) ? Infinity : time
    }

    Object.values(robotTasks).forEach(rTasks => {
      for (let i = 0; i < rTasks.length; i++) {
        for (let j = i + 1; j < rTasks.length; j++) {
          const a = rTasks[i]
          const b = rTasks[j]

          const aDays = a.scheduler_settings?.weekdays || {}
          const bDays = b.scheduler_settings?.weekdays || {}
          const sharedDay = Object.keys(aDays).some(day => aDays[day] === 'true' && bDays[day] === 'true')

          if (sharedDay) {
            const aStart = getTimeInMinutes(a.scheduler_settings?.datetime)
            const aDuration = (a.predicted_duration || a.execution_time || 0) / 60
            const aEnd = aStart + aDuration

            const bStart = getTimeInMinutes(b.scheduler_settings?.datetime)
            const bDuration = (b.predicted_duration || b.execution_time || 0) / 60
            const bEnd = bStart + bDuration

            if (aStart < bEnd && bStart < aEnd) {
              overlaps.add(a.id)
              overlaps.add(b.id)
            }
          }
        }
      }
    })

    return tasks.map(t => ({ ...t, isOverlapping: overlaps.has(t.id) }))
  }, [odysseys, referencedScenarios])

  const scheduledTasksForCleaning = useMemo(
    () => (vendorUiLayout.cleaning.showAutonomicsScheduledTasks ? scheduledTasks : []),
    [vendorUiLayout.cleaning.showAutonomicsScheduledTasks, scheduledTasks]
  )

  const visibleMissionIds = useMemo(() => {
    const s = new Set<number>()
    scheduledTasksForCleaning.forEach(t => {
      if (!hiddenScheduledTaskIds.has(t.id)) {
        ;(t.missions || []).forEach((id: number) => s.add(id))
      }
    })
    return s
  }, [scheduledTasksForCleaning, hiddenScheduledTaskIds])

  useEffect(() => {
    setHiddenScheduledTaskIds(prev => {
      let changed = false
      const next = new Set<number>()
      prev.forEach(id => {
        if (scheduledTasks.some(t => t.id === id)) next.add(id)
        else changed = true
      })
      return changed ? next : prev
    })
  }, [scheduledTasks])

  useEffect(() => {
    const fetchMultipleMissions = async () => {
      const missionsToFetch = Array.from(visibleMissionIds)
      if (activeTab === 0 && missionsToFetch.length > 0) {
        try {
          const promises = missionsToFetch.map(id => {
            if (missionCacheRef.current[id]) {
              return Promise.resolve(missionCacheRef.current[id])
            }
            if (!missionPromises[id]) {
              missionPromises[id] = MainApi.fetchMission(id)
            }
            return missionPromises[id]
          })
          const results = await Promise.all(promises)

          let updated = false
          results.forEach(m => {
            if (!missionCacheRef.current[m.mission.id]) {
              missionCacheRef.current[m.mission.id] = m
              updated = true
            }
          })
          if (updated) setCacheVersion(v => v + 1)

          setScheduledMissionsDetails(results)
        } catch (error) {
          console.error('Failed to fetch multiple missions', error)
        }
      } else if (activeTab === 0 && missionsToFetch.length === 0) {
        setScheduledMissionsDetails([])
      }
    }
    fetchMultipleMissions()
  }, [hiddenScheduledTaskIds, visibleMissionIds, activeTab])

  useEffect(() => {
    setHiddenScheduledTaskIds(new Set())
    setHiddenGausiumExecTaskIds(new Set())
  }, [facility?.id])

  const aggregateStats = useMemo(() => {
    let coverage = 0
    let length = 0
    let time = 0

    // For aggregate, we sum up all non-hidden scheduled tasks (Odysseys)
    scheduledTasksForCleaning.forEach(task => {
      if (hiddenScheduledTaskIds.has(task.id)) return

      const stats = task.average_statistics
      if (stats) {
        coverage += stats.cleaned_area_actual || task.overall_cleaned_area || 0
        length += stats.travel_distance_actual || task.overall_length || 0
      } else {
        coverage += task.overall_cleaned_area || 0
        length += task.overall_length || 0
      }
      time += task.predicted_duration || task.execution_time || 0
    })
    return { coverage, length, time }
  }, [scheduledTasksForCleaning, hiddenScheduledTaskIds])

  const coloredScheduledCleaningRoutes = useMemo(() => {
    return scheduledMissionsDetails.flatMap(m =>
      m.cleaningRoutes.map(route => ({
        ...route,
        properties: {
          ...route.properties,
          highlighted: highlightedMissionIds.includes(m.mission.id),
        },
      }))
    )
  }, [scheduledMissionsDetails, highlightedMissionIds])

  // Assets tab state
  const [robotsPage, setRobotsPage] = useState(1)
  const [robotsPageInput, setRobotsPageInput] = useState('1')
  const [stationsPage, setStationsPage] = useState(1)
  const [stationsPageInput, setStationsPageInput] = useState('1')
  const [maintenancePage, setMaintenancePage] = useState(1)
  const [maintenancePageInput, setMaintenancePageInput] = useState('1')

  // Scenarios tab state
  const navigate = useNavigate()
  const [scenarios, setScenarios] = useState<any[]>([])
  const [scenariosProgress, setScenariosProgress] = useState(PROGRESS_TYPES.IDLE)
  const [highlightedScenarioId, setHighlightedScenarioId] = useState<number | undefined>(undefined)
  const [selectedScenarioById, setSelectedScenarioById] = useState<any | null>(null)
  const [gausiumPreviewDismissed, setGausiumPreviewDismissed] = useState(false)
  const mapWrapperRef = useRef<HTMLDivElement>(null)
  const [scenariosPage, setScenariosPage] = useState(1)
  const [scenariosPageInput, setScenariosPageInput] = useState('1')
  const [scenariosTotalCount, setScenariosTotalCount] = useState(0)
  const [scenariosRefreshKey, setScenariosRefreshKey] = useState(0)

  useEffect(() => {
    if (!isMixedVendorFacility) return
    setHighlightedScenarioId(undefined)
    setSelectedScenarioById(null)
    setScenariosPage(1)
    setGausiumPreviewDismissed(true)
    setHighlightedMissionIds([])
    setHighlightedGausiumExecId(undefined)
    hasZoomedAllCleaning.current = false
    hasZoomedGausiumCleaningMaps.current = false
  }, [facilityScenariosHistoryVendor, isMixedVendorFacility])

  const [gausiumExecTasks, setGausiumExecTasks] = useState<
    Array<{
      id: number
      robot_id: number
      robot_sn?: string
      vendor_task_uuid?: string
      task_name?: string
      map_name?: string
      gausium_map_id?: number | null
    }>
  >([])
  const [gausiumExecProgress, setGausiumExecProgress] = useState(PROGRESS_TYPES.IDLE)
  const [gausiumMapGeoPins, setGausiumMapGeoPins] = useState<GausiumMapGeoPin[]>([])
  const [gausiumRecentReports, setGausiumRecentReports] = useState<
    Array<{
      robot_sn: string
      task_name: string
      cleaned_area_actual: number
      cleaned_area_planned: number
      duration_actual: number
      duration_overall: number
    }>
  >([])

  useEffect(() => {
    if (!GAUSIUM_ENABLED || !hasGausiumRobots || activeTab !== 0 || !facility?.id) {
      setGausiumExecTasks([])
      setGausiumExecProgress(PROGRESS_TYPES.IDLE)
      return
    }
    let cancelled = false
    setGausiumExecProgress(PROGRESS_TYPES.WORK)
    MainApi.fetchFacilityGausiumExecutableTasks(facility.id)
      .then(rows => {
        if (!cancelled) {
          setGausiumExecTasks(rows)
          setGausiumExecProgress(PROGRESS_TYPES.SUCCESS)
        }
      })
      .catch(() => {
        if (!cancelled) setGausiumExecProgress(PROGRESS_TYPES.ERROR)
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, facility?.id, hasGausiumRobots])

  useEffect(() => {
    if (!GAUSIUM_ENABLED || !hasGausiumRobots || activeTab !== 0) {
      setGausiumRecentReports([])
      return
    }

    const robotSns = new Set(
      (robots ?? [])
        .filter(r => r.vendor === VENDOR.GAUSIUM)
        .map(r => String(r.serialNumber || '').trim())
        .filter(Boolean)
    )
    if (robotSns.size === 0) {
      setGausiumRecentReports([])
      return
    }

    let cancelled = false
    const end = new Date()
    const start = new Date(end.getTime() - TWO_WEEKS_MS)
    const toReportRows = (list: any[]) =>
      list
        .map((r: any) => ({
          robot_sn: String(r?.robot_sn || '').trim(),
          task_name: String(r?.name || '').trim(),
          cleaned_area_actual: Number(r?.cleaned_area_actual || 0),
          cleaned_area_planned: Number(r?.cleaned_area_planned || 0),
          duration_actual: Number(r?.duration_actual || 0),
          duration_overall: Number(r?.duration_overall || r?.duration_actual || 0),
        }))
        .filter((r: any) => !!r.robot_sn && !!r.task_name && robotSns.has(r.robot_sn))

    MainApi.fetchUnifiedEvents({
      start_date: start.toISOString(),
      end_date: end.toISOString(),
      source: 'gausium_task_reports',
      cache_only: true,
      ...(gausiumRobotIds.length ? { robot_ids: gausiumRobotIds } : {}),
    })
      .then(res => {
        if (cancelled) return
        const rows = Array.isArray(res?.results) ? res.results : []
        setGausiumRecentReports(toReportRows(rows))
      })
      .catch(() => {
        if (!cancelled) setGausiumRecentReports([])
      })

    return () => {
      cancelled = true
    }
  }, [activeTab, hasGausiumRobots, robots, gausiumRobotIds])

  const gausiumExecTaskKeys = useMemo(() => {
    const keys = new Set<string>()
    gausiumExecTasks.forEach(t => {
      const taskName = String(t.task_name || '').trim()
      const sn = String(t.robot_sn || '').trim()
      if (taskName && sn) keys.add(`${sn}::${taskName}`)
    })
    return keys
  }, [gausiumExecTasks])

  const gausiumExecTasksFiltered = useMemo(() => {
    if (!GAUSIUM_ENABLED || !hasGausiumRobots) return []
    const seenKeys = new Set<string>()
    gausiumRecentReports.forEach(r => {
      const taskName = String(r.task_name || '').trim()
      const sn = String(r.robot_sn || '').trim()
      if (taskName && sn) seenKeys.add(`${sn}::${taskName}`)
    })
    return gausiumExecTasks.filter(t => {
      const taskName = String(t.task_name || '').trim()
      const sn = String(t.robot_sn || '').trim()
      return !!taskName && !!sn && seenKeys.has(`${sn}::${taskName}`)
    })
  }, [gausiumExecTasks, gausiumRecentReports, hasGausiumRobots])

  const gausiumExecTasksForCleaning = useMemo(() => {
    if (!GAUSIUM_ENABLED || !hasGausiumRobots) return []
    if (!vendorUiLayout.cleaning.showGausiumExecutableMirrorRows) return []
    return gausiumExecTasksFiltered
  }, [
    GAUSIUM_ENABLED,
    hasGausiumRobots,
    vendorUiLayout.cleaning.showGausiumExecutableMirrorRows,
    gausiumExecTasksFiltered,
  ])

  useEffect(() => {
    const validIds = new Set(gausiumExecTasksForCleaning.map(t => t.id))
    setHiddenGausiumExecTaskIds(prev => {
      let changed = false
      const next = new Set<number>()
      prev.forEach(id => {
        if (validIds.has(id)) next.add(id)
        else changed = true
      })
      return changed ? next : prev
    })
  }, [gausiumExecTasksForCleaning])

  const visibleGausiumMapIdsForPins = useMemo(() => {
    const s = new Set<number>()
    gausiumExecTasksForCleaning.forEach(t => {
      if (t.gausium_map_id == null || hiddenGausiumExecTaskIds.has(t.id)) return
      s.add(Number(t.gausium_map_id))
    })
    return s
  }, [gausiumExecTasksForCleaning, hiddenGausiumExecTaskIds])

  const gausiumMapPinsOnCleaningTab = useMemo(
    () => gausiumMapGeoPins.filter(p => visibleGausiumMapIdsForPins.has(Number(p.id))),
    [gausiumMapGeoPins, visibleGausiumMapIdsForPins]
  )

  /** GS-only cleaning tab: fit map to geo locations of Gausium maps referenced by executable tasks (Autonomics path uses mission geometry above). */
  useEffect(() => {
    if (
      !map ||
      activeTab !== 0 ||
      !GAUSIUM_ENABLED ||
      !hasGausiumRobots ||
      hasZoomedGausiumCleaningMaps.current ||
      scheduledTasksForCleaning.length > 0 ||
      scheduledMissionsDetails.length > 0
    ) {
      return
    }
    const mapIds = new Set(
      gausiumExecTasksForCleaning.map(t => t.gausium_map_id).filter((id): id is number => id != null)
    )
    if (mapIds.size === 0 || gausiumMapGeoPins.length === 0) {
      return
    }
    const features: Feature[] = []
    gausiumMapGeoPins.forEach(pin => {
      if (!mapIds.has(pin.id)) return
      const gp = pin.geo_point
      if (gp?.type === 'Point' && gp.coordinates?.length === 2) {
        const [lng, lat] = gp.coordinates as [number, number]
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {},
        })
      }
    })
    if (features.length === 0) {
      return
    }
    zoomMapTo(map, features, isMobile ? 0 : 580)
    hasZoomedGausiumCleaningMaps.current = true
  }, [
    map,
    activeTab,
    hasGausiumRobots,
    scheduledTasksForCleaning.length,
    scheduledMissionsDetails.length,
    gausiumExecTasksForCleaning,
    gausiumMapGeoPins,
  ])

  const isAllCleaningSelected = useMemo(() => {
    if (vendorUiLayout.cleaning.showAutonomicsScheduledTasks) {
      if (scheduledTasksForCleaning.length === 0) return true
      return scheduledTasksForCleaning.every(t => !hiddenScheduledTaskIds.has(t.id))
    }
    if (gausiumExecTasksForCleaning.length === 0) return true
    return gausiumExecTasksForCleaning.every(t => !hiddenGausiumExecTaskIds.has(t.id))
  }, [
    vendorUiLayout.cleaning.showAutonomicsScheduledTasks,
    scheduledTasksForCleaning,
    gausiumExecTasksForCleaning,
    hiddenScheduledTaskIds,
    hiddenGausiumExecTaskIds,
  ])

  const toggleShowAllCleaning = () => {
    if (isAllCleaningSelected) {
      if (vendorUiLayout.cleaning.showAutonomicsScheduledTasks) {
        setHiddenScheduledTaskIds(new Set(scheduledTasksForCleaning.map(t => t.id)))
      }
      if (vendorUiLayout.cleaning.showGausiumExecutableMirrorRows) {
        setHiddenGausiumExecTaskIds(new Set(gausiumExecTasksForCleaning.map(t => t.id)))
      }
    } else {
      if (vendorUiLayout.cleaning.showAutonomicsScheduledTasks) {
        setHiddenScheduledTaskIds(new Set())
      }
      if (vendorUiLayout.cleaning.showGausiumExecutableMirrorRows) {
        setHiddenGausiumExecTaskIds(new Set())
      }
    }
  }

  const gausiumRecentReportsForExecutable = useMemo(
    () => gausiumRecentReports.filter(r => gausiumExecTaskKeys.has(`${r.robot_sn}::${r.task_name}`)),
    [gausiumRecentReports, gausiumExecTaskKeys]
  )

  const gausiumAggregateAverages = useMemo(() => {
    if (gausiumRecentReportsForExecutable.length === 0) return { coverage: 0, length: 0, time: 0 }
    const byTask = new Map<string, Array<typeof gausiumRecentReportsForExecutable[number]>>()
    gausiumRecentReportsForExecutable.forEach(r => {
      const key = `${r.robot_sn}::${r.task_name}`
      const bucket = byTask.get(key)
      if (bucket) bucket.push(r)
      else byTask.set(key, [r])
    })

    let coverage = 0
    let plannedCoverage = 0
    let time = 0
    byTask.forEach(rows => {
      const n = rows.length || 1
      const coverageAvg = rows.reduce((acc, r) => acc + (r.cleaned_area_actual || 0), 0) / n
      const plannedCoverageAvg = rows.reduce((acc, r) => acc + (r.cleaned_area_planned || 0), 0) / n
      const timeAvg = rows.reduce((acc, r) => acc + (r.duration_overall || r.duration_actual || 0), 0) / n
      coverage += coverageAvg
      plannedCoverage += plannedCoverageAvg
      time += timeAvg
    })

    return { coverage, length: plannedCoverage, time }
  }, [gausiumRecentReportsForExecutable])

  const aggregateStatsForView = useMemo(() => {
    if (scheduledTasksForCleaning.length > 0) return aggregateStats
    if (GAUSIUM_ENABLED && vendorUiLayout.cleaning.showGausiumExecutableMirrorRows) return gausiumAggregateAverages
    return aggregateStats
  }, [
    aggregateStats,
    gausiumAggregateAverages,
    scheduledTasksForCleaning.length,
    vendorUiLayout.cleaning.showGausiumExecutableMirrorRows,
  ])

  useEffect(() => {
    if (!GAUSIUM_ENABLED || !hasGausiumRobots || gausiumRobotIds.length === 0) {
      setGausiumMapGeoPins([])
      return
    }
    if (activeTab !== 0 && activeTab !== 2 && activeTab !== 3) {
      return
    }
    let cancelled = false
    MainApi.fetchGausiumMaps(gausiumRobotIds)
      .then(({ results }) => {
        if (cancelled) return
        const mapped = results.map(m => ({
          id: m.id,
          name: m.map_name || '',
          geo_point: m.geo_point ?? null,
        }))
        setGausiumMapGeoPins(mapped)
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('[FacilityView] fetchGausiumMaps (pins):', err)
          setGausiumMapGeoPins([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, hasGausiumRobots, gausiumRobotIds])

  const gausiumMapPinHighlightId = useMemo(() => {
    if (highlightedScenarioId == null) return undefined
    const row = scenarios.find(s => s.id === highlightedScenarioId)
    if (row?.is_gausium_executable_task && row.gausium_map_pk != null) return row.gausium_map_pk
    return highlightedScenarioId
  }, [highlightedScenarioId, scenarios])

  const gausiumPinsForMap = useMemo(() => {
    if (activeTab === 0) return gausiumMapPinsOnCleaningTab
    if (activeTab === 2 || activeTab === 3) {
      if (!vendorUiLayout.map.showGausiumMapPinsOnScenariosHistory) {
        return []
      }
    }
    return gausiumMapGeoPins
  }, [
    activeTab,
    gausiumMapPinsOnCleaningTab,
    gausiumMapGeoPins,
    vendorUiLayout.map.showGausiumMapPinsOnScenariosHistory,
  ])

  /** Cleaning + Scenarios + History: move camera to the selected GS map pin when row/scenario is selected. */
  useEffect(() => {
    if (!map || !GAUSIUM_ENABLED || !hasGausiumRobots) return
    if (activeTab !== 0 && activeTab !== 2 && activeTab !== 3) return
    if (highlightedScenarioId == null) {
      lastGausiumCameraFocusScenarioId.current = undefined
      return
    }
    const pin = gausiumPinsForMap.find(p => Number(p.id) === Number(gausiumMapPinHighlightId))
    const gp = pin?.geo_point
    if (gp?.type !== 'Point' || !gp.coordinates || gp.coordinates.length < 2) return
    const [lng, lat] = gp.coordinates as [number, number]
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return

    if (lastGausiumCameraFocusScenarioId.current === highlightedScenarioId) return
    lastGausiumCameraFocusScenarioId.current = highlightedScenarioId

    zoomMapTo(
      map,
      [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {},
        },
      ],
      isMobile ? 0 : SCENARIO_MAP_COLUMN_INSET_LEFT_PX,
      17
    )
  }, [map, activeTab, highlightedScenarioId, gausiumPinsForMap, hasGausiumRobots, gausiumMapPinHighlightId])

  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false)
  const [gausiumLaunchMapId, setGausiumLaunchMapId] = useState<number | null>(null)
  const [gausiumLaunchExecTask, setGausiumLaunchExecTask] = useState<GausiumExecutableTaskRecent | null>(null)
  /** When launching from a Cleaning-row GS task, prefer this robot; scenarios tab uses facility default. */
  const [gausiumLaunchRobotId, setGausiumLaunchRobotId] = useState<number | undefined>(undefined)
  const [launchItem, setLaunchItem] = useState<{ scenario?: any; mission?: IMissionInfo; task?: any } | undefined>(
    undefined
  )
  const [taskToEdit, setTaskToEdit] = useState<any | undefined>(undefined)
  const [initialTab, setInitialTab] = useState<number | undefined>(undefined)

  const handleSubmitCancelForToday = async (): Promise<void> => {
    try {
      if (!taskToCancel) return
      const dateStr = toISOStringYYYYMMDD(new Date())
      const foundTask = taskToCancel

      if (foundTask && foundTask.scheduler_settings) {
        const newSchedulerSettings = {
          ...foundTask.scheduler_settings,
          except_days: [...(foundTask.scheduler_settings.except_days || [])],
          except_days_descriptions: { ...(foundTask.scheduler_settings.except_days_descriptions || {}) },
          except_days_users: { ...(foundTask.scheduler_settings.except_days_users || {}) },
        }

        if (!newSchedulerSettings.except_days.includes(dateStr)) {
          newSchedulerSettings.except_days.push(dateStr)
          newSchedulerSettings.except_days.sort()
        }

        const finalDescription =
          deleteReason === TASK_CANCEL_REASON_OTHER ? deleteDescription : deleteReason
        newSchedulerSettings.except_days_descriptions[dateStr] = finalDescription

        const userName = user?.state.firstName
          ? `${user.state.firstName} ${user.state.lastName || ''}`.trim()
          : user?.state.email || 'Unknown User'
        newSchedulerSettings.except_days_users[dateStr] = userName

        await RobotApi.updateTheEntireOdyssey(
          foundTask.id,
          foundTask.missions,
          foundTask.robot_id,
          newSchedulerSettings,
          foundTask.name ? foundTask.name : 'Cleaning Task',
          foundTask.scenario,
          TASK_STATUSES.NEW
        )

        onRobotsUpdated()
        setOpenCancelDialog(false)
        setDeleteReason('')
        setDeleteDescription('')
        setTaskToCancel(null)
      }
    } catch (e) {
      console.error(`Failed to cancel task for today: ${e}`)
    }
  }

  useEffect(() => {
    setScenariosPageInput(scenariosPage.toString())
  }, [scenariosPage])

  useEffect(() => {
    setRobotsPageInput(robotsPage.toString())
  }, [robotsPage])

  useEffect(() => {
    setStationsPageInput(stationsPage.toString())
  }, [stationsPage])

  useEffect(() => {
    setMaintenancePageInput(maintenancePage.toString())
  }, [maintenancePage])

  const handleScenariosPageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(scenariosPageInput)
      const maxPage = Math.ceil(scenariosTotalCount / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setScenariosPage(val)
      } else {
        setScenariosPageInput(scenariosPage.toString())
      }
    }
  }

  const handleRobotsPageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(robotsPageInput)
      const maxPage = Math.ceil(robots.length / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setRobotsPage(val)
      } else {
        setRobotsPageInput(robotsPage.toString())
      }
    }
  }

  const handleStationsPageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(stationsPageInput)
      const maxPage = Math.ceil(stations.length / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setStationsPage(val)
      } else {
        setStationsPageInput(stationsPage.toString())
      }
    }
  }

  const handleMaintenancePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(maintenancePageInput)
      const maxPage = Math.ceil(maintenancePoints.length / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setMaintenancePage(val)
      } else {
        setMaintenancePageInput(maintenancePage.toString())
      }
    }
  }

  const handleGausiumScenariosSync = useCallback((allScenarios: any[], totalCount: number) => {
    setScenarios(allScenarios)
    setScenariosTotalCount(totalCount)
  }, [])

  useEffect(() => {
    if (activeTab !== 2) return
    if (vendorUiLayout.scenarios.isGausiumMapsMode) return
    let cancelled = false
    const run = async () => {
      try {
        setScenariosProgress(PROGRESS_TYPES.WORK)
        if (!facility?.id) {
          if (!cancelled) {
            setScenarios([])
            setScenariosTotalCount(0)
            setScenariosProgress(PROGRESS_TYPES.SUCCESS)
          }
          return
        }
        const { results, count } = await MainApi.fetchScenarios(
          scenariosPage,
          PAGE_SIZE,
          undefined,
          undefined,
          undefined,
          facility.id
        ).catch(err => {
          if (err?.status === 404 || err?.response?.status === 404) return { results: [], count: 0 }
          throw err
        })
        if (!cancelled) {
          setScenarios(results)
          setScenariosTotalCount(count)
          setScenariosProgress(PROGRESS_TYPES.SUCCESS)
        }
      } catch (error) {
        if (!cancelled) setScenariosProgress(PROGRESS_TYPES.ERROR)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [
    activeTab,
    facility?.id,
    scenariosPage,
    scenariosRefreshKey,
    vendorUiLayout.scenarios.isGausiumMapsMode,
  ])

  useEffect(() => {
    if (activeTab === 0 && odysseys.length > 0) {
      const scenarioIds = Array.from(new Set(odysseys.map(o => o.scenario).filter(Boolean))) as number[]
      if (scenarioIds.length > 0) {
        const fetchRefs = async () => {
          try {
            const promises = scenarioIds.map(id => MainApi.fetchScenario(id))
            const results = await Promise.all(promises)
            setReferencedScenarios(results)
          } catch (e) {
            console.warn('[FacilityView] fetch referenced scenarios error:', e)
          }
        }
        fetchRefs()
      }
    }
  }, [activeTab, odysseys])

  const handleScenarioSelected = async (scenario: any) => {
    try {
      if (scenario?.id && facility?.id) {
        await MainApi.patchScenarios(scenario.id, { facility_id: facility.id })
        setScenariosRefreshKey(prev => prev + 1)
      }
      setIsSelectScenarioModalOpen(false)
    } catch (e) {
      console.error('[FacilityView] Add scenario error:', e)
    }
  }

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) {
      setMap(m)
    } else {
      console.warn('[FacilityView] onMapInited: getMap() empty')
    }
  }

  const selectedGausiumScenarioPreview = useMemo(() => {
    if ((activeTab !== 2 && activeTab !== 3) || !highlightedScenarioId) return null
    const s =
      scenarios.find(x => x.id === highlightedScenarioId) ??
      (selectedScenarioById && selectedScenarioById.id === highlightedScenarioId ? selectedScenarioById : null)
    if (s?.is_gausium_executable_task) {
      return { url: s.preview_url as string | null | undefined, name: String(s.name || '') }
    }
    if (!s?.is_gausium_map) return null
    return { url: s.preview_url as string | null | undefined, name: String(s.name || '') }
  }, [activeTab, highlightedScenarioId, scenarios, selectedScenarioById])

  useEffect(() => {
    if (activeTab !== 2 && activeTab !== 3) return
    if (!highlightedScenarioId) {
      setSelectedScenarioById(null)
      return
    }

    if (vendorUiLayout.scenarios.isGausiumMapsMode) {
      // GS maps are not part of /scenario/{id}/; rely on paginated GS maps list instead.
      setSelectedScenarioById(null)
      return
    }

    // If selected scenario/map is not on the current page, fetch it by id for preview.
    const foundOnPage = scenarios.some(s => s?.id === highlightedScenarioId)
    if (!foundOnPage) {
      MainApi.fetchScenario(highlightedScenarioId)
        .then(s => setSelectedScenarioById(s))
        .catch(() => undefined)
    } else {
      setSelectedScenarioById(null)
    }
  }, [activeTab, highlightedScenarioId, scenarios, vendorUiLayout.scenarios.isGausiumMapsMode])

  useEffect(() => {
    // Auto-paginate to selected item when scenarios are paginated (non-GS-maps-in-scenarios mode).
    if (activeTab !== 2 && activeTab !== 3) return
    if (vendorUiLayout.scenarios.isGausiumMapsMode) return
    if (!highlightedScenarioId) return
    if (scenarios.some(s => s?.id === highlightedScenarioId)) return

    const maxPage = Math.ceil((scenariosTotalCount || 0) / PAGE_SIZE) || 1
    let cancelled = false

    const findPage = async () => {
      if (!facility?.id) return
      for (let page = 1; page <= maxPage; page++) {
        if (cancelled) return
        const { results } = await MainApi.fetchScenarios(
          page,
          PAGE_SIZE,
          undefined,
          undefined,
          undefined,
          facility.id
        ).catch(err => {
          if (err?.status === 404 || err?.response?.status === 404) return { results: [], count: 0 }
          throw err
        })
        if (results.some(s => s?.id === highlightedScenarioId)) {
          setScenariosPage(page)
          return
        }
      }
    }

    findPage().catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [
    activeTab,
    highlightedScenarioId,
    vendorUiLayout.scenarios.isGausiumMapsMode,
    facility?.id,
    scenariosTotalCount,
    scenarios,
    setScenariosPage,
  ])

  useEffect(() => {
    // GS-only scenarios tab: full task list is synced — jump to the page that contains the selection.
    if (activeTab !== 2 && activeTab !== 3) return
    if (!highlightedScenarioId) return
    if (!vendorUiLayout.scenarios.isGausiumMapsMode) return
    const idx = scenarios.findIndex(s => s?.id === highlightedScenarioId)
    if (idx < 0) return
    const page = Math.floor(idx / PAGE_SIZE) + 1
    if (page !== scenariosPage) setScenariosPage(page)
  }, [
    activeTab,
    highlightedScenarioId,
    vendorUiLayout.scenarios.isGausiumMapsMode,
    scenarios,
    scenariosPage,
    setScenariosPage,
  ])

  // Only reset dismissed when user selects another row — not on tab switch (avoids reopening preview when returning to Scenarios).
  useEffect(() => {
    setGausiumPreviewDismissed(false)
  }, [highlightedScenarioId])

  const handleScenarioSelect = useCallback(
    (id: number) => {
      // Re-click on the same GS map should re-open preview after reset/close.
      if (highlightedScenarioId === id) {
        setHighlightedScenarioId(undefined)
        setGausiumPreviewDismissed(false)
        setTimeout(() => setHighlightedScenarioId(id), 0)
        return
      }
      setHighlightedScenarioId(id)
      setGausiumPreviewDismissed(false)
    },
    [activeTab, highlightedScenarioId]
  )

  const address = [facility.street_address, facility.city, facility.country].filter(Boolean).join(', ')

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.titleBlock} style={{ pointerEvents: 'none' }}>
          <div className={style.titleInner} style={{ pointerEvents: 'auto' }}>
            <div className={style.titleMainRow}>
              <div className={style.titleWithFavorite}>
                <Title text={facility.name} className={style.mobileTitle} />
                {facility.id > 0 && <FavoriteStar kind="facility" id={facility.id} />}
              </div>
              {isMobile && (
                <div className={style.mobileHeaderControls}>
                  <Dropdown
                    placeholder={formatUtcOffset(displayOffsetMinutes)}
                    currentOption={{
                      value: displayOffsetMinutes,
                      text: formatUtcOffset(displayOffsetMinutes),
                    }}
                    options={UTC_OFFSET_OPTIONS}
                    onClickOption={(opt: IOption) => setDisplayOffsetMinutes(Number(opt.value))}
                    minWidth={isMobile ? 120 : 160}
                    useSmallDropdownStyle={isMobile}
                    className={style.timezoneDropdownMobile}
                  />
                  <button
                    className={style.headerButton}
                    title="Settings"
                    onClick={() => setIsEditFacilityModalOpen(true)}
                  >
                    <GearSvg />
                  </button>
                </div>
              )}
            </div>
            {!isMobile && address && (
              <div className={style.address}>
                {address}
                {/* @ts-ignore */}
                {facility.timezone && ` (Timezone: ${facility.timezone})`}
              </div>
            )}
          </div>
          {!isMobile && activeTab === 2 && !vendorUiLayout.scenarios.isGausiumMapsMode && (
            <div className={style.headerRight} style={{ pointerEvents: 'auto' }}>
              <Button.Standart className={style.button} onClick={() => setIsSelectScenarioModalOpen(true)}>
                Add a Scenario
              </Button.Standart>
            </div>
          )}
          {!isMobile && activeTab === 1 && assetsSubTab === 'maintenance' && (
            <div className={style.headerRight} style={{ pointerEvents: 'auto' }}>
              <Button.Standart className={style.button} onClick={() => setAddingNewMaintenancePoint(true)}>
                Add maintenance point
              </Button.Standart>
            </div>
          )}
        </div>

        <div className={style.filtersRight}>
          <Dropdown
            placeholder={formatUtcOffset(displayOffsetMinutes)}
            currentOption={{
              value: displayOffsetMinutes,
              text: formatUtcOffset(displayOffsetMinutes),
            }}
            options={UTC_OFFSET_OPTIONS}
            onClickOption={(opt: IOption) => setDisplayOffsetMinutes(Number(opt.value))}
            minWidth={160}
          />
          {isMixedVendorFacility && (activeTab === 0 || activeTab === 2 || activeTab === 3) && (
            <Dropdown
              placeholder={t('common.vendor', 'Vendor')}
              currentOption={{
                value: facilityScenariosHistoryVendor,
                text: getVendorDisplayName(facilityScenariosHistoryVendor),
              }}
              options={facilityVendorDropdownOptions}
              onClickOption={(opt: IOption) => setFacilityScenariosHistoryVendor(String(opt.value) as VendorCode)}
              minWidth={160}
            />
          )}
          <button className={style.headerButton} title="Settings" onClick={() => setIsEditFacilityModalOpen(true)}>
            <GearSvg />
          </button>
        </div>
        <Box className={clsx(style.box, { [style.boxHidden]: isMobile && !showSidebar })}>
          {/* Tabs */}
          <AdaptiveTabs
            value={muiTabValue}
            onChange={(_, v) => {
              const logicalTab = muiValueToFacilityTab(
                v,
                vendorUiLayout.tabs.showScenariosTab,
                vendorUiLayout.tabs.showHistoryTab
              )
              setActiveTab(logicalTab)
              setHighlightedMissionIds([])
              setHighlightedRobotId(undefined)
              setHighlightedGausiumExecId(undefined)
              setSelectedMissionsDetails([])
              if (logicalTab !== FACILITY_TAB.SCENARIOS) {
                setHighlightedScenarioId(undefined)
                setGausiumPreviewDismissed(true)
              }
            }}
            indicatorColor="primary"
            sx={{ height: 45, minHeight: 45 }}
          >
            <StyledTab
              label="Cleaning"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
              disableRipple
            />
            <StyledTab
              label={t('header.assets')}
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
              disableRipple
            />
            {vendorUiLayout.tabs.showScenariosTab && (
              <StyledTab
                label="Scenarios"
                sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
                disableRipple
              />
            )}
            {vendorUiLayout.tabs.showHistoryTab && (
              <StyledTab
                label={t('robot.history')}
                sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
                disableRipple
              />
            )}
          </AdaptiveTabs>

          {/* Tab content */}
          {activeTab === 0 && (
            <div
              className={style.missionsTab}
              onClick={() => {
                setHighlightedMissionIds([])
                setHighlightedGausiumExecId(undefined)
              }}
            >
              {missionsProgress === PROGRESS_TYPES.WORK && <Loader />}
              {missionsProgress === PROGRESS_TYPES.ERROR && (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>Error loading scheduled missions</div>
                </div>
              )}
              {missionsProgress === PROGRESS_TYPES.SUCCESS && (
                <>
                  <MissionStats
                    coverage={aggregateStatsForView.coverage}
                    length={aggregateStatsForView.length}
                    time={aggregateStatsForView.time}
                    lengthLabel={
                      GAUSIUM_ENABLED &&
                      vendorUiLayout.cleaning.showGausiumExecutableMirrorRows &&
                      scheduledTasksForCleaning.length === 0
                        ? isMobile
                          ? 'Plan Coverage'
                          : 'Planned Coverage'
                        : 'Length'
                    }
                    lengthSuffix={
                      GAUSIUM_ENABLED &&
                      vendorUiLayout.cleaning.showGausiumExecutableMirrorRows &&
                      scheduledTasksForCleaning.length === 0
                        ? 'm2'
                        : 'm'
                    }
                    lengthPrecision={
                      GAUSIUM_ENABLED &&
                      vendorUiLayout.cleaning.showGausiumExecutableMirrorRows &&
                      scheduledTasksForCleaning.length === 0
                        ? 0
                        : 0
                    }
                  />
                  <div
                    className={clsx(style.cleaningTableHeader, {
                      [style.cleaningTableHeaderGausium]:
                        GAUSIUM_ENABLED &&
                        vendorUiLayout.cleaning.showGausiumExecutableMirrorRows &&
                        scheduledTasksForCleaning.length === 0,
                    })}
                  >
                    <div></div>
                    <div className={clsx(style.missionsHeaderCol, 'text-16-400')}>Task</div>
                    <div className={clsx(style.missionsHeaderCol, 'text-16-400')}>Mission/Scenario</div>
                    <div className={clsx(style.missionsHeaderCol, 'text-16-400')}>Schedule</div>
                    <div className={clsx(style.colCenter, 'text-16-400')}>
                      <input
                        type="checkbox"
                        checked={isAllCleaningSelected}
                        onChange={toggleShowAllCleaning}
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                    <div className={style.colCenter}></div>
                  </div>
                  {scheduledTasksForCleaning.length > 0 ||
                  (GAUSIUM_ENABLED &&
                    vendorUiLayout.cleaning.showGausiumExecutableMirrorRows &&
                    gausiumExecTasksForCleaning.length > 0) ? (
                    <div className={style.table}>
                      {scheduledTasksForCleaning.map((task: any) => {
                        const uniqueKey = `odyssey_${task.id}`
                        const missionIds = task.missions || []
                        const isChecked = !hiddenScheduledTaskIds.has(task.id)

                        return (
                          <FacilityCleaningTaskItem
                            key={uniqueKey}
                            task={{ ...task, isOdyssey: true } as any}
                            scheduledMissionsDetails={scheduledMissionsDetails}
                            referencedScenarios={referencedScenarios}
                            onClick={() => {
                              if (missionIds.length > 0) {
                                setHighlightedMissionIds(missionIds)
                              }
                            }}
                            highlighted={
                              missionIds.length > 0 &&
                              (missionIds as number[]).every((id: number) => highlightedMissionIds.includes(id))
                            }
                            showCheckbox={true}
                            checked={isChecked}
                            onCheckboxChange={(checked: boolean) => {
                              setHiddenScheduledTaskIds(prev => {
                                const next = new Set(prev)
                                if (checked) next.delete(task.id)
                                else next.add(task.id)
                                return next
                              })
                            }}
                            onLaunch={task => {
                              setLaunchItem({ task })
                              setIsLaunchModalOpen(true)
                            }}
                            onReschedule={task => {
                              setTaskToEdit(task)
                              setInitialTab(1)
                              setIsLaunchModalOpen(true)
                            }}
                            onCancelForToday={task => {
                              setTaskToCancel(task)
                              setDeleteReason('')
                              setDeleteDescription('')
                              setOpenCancelDialog(true)
                            }}
                            isOverlapping={task.isOverlapping}
                            displayOffsetMinutes={displayOffsetMinutes}
                          />
                        )
                      })}
                      {GAUSIUM_ENABLED &&
                        vendorUiLayout.cleaning.showGausiumExecutableMirrorRows &&
                        gausiumExecTasksForCleaning.map(task => (
                          <FacilityCleaningTaskItem
                            key={`gs_exec_${task.id}`}
                            task={{
                              ...(task as any),
                              isGausiumMirror: true,
                              name: task.task_name || '',
                            }}
                            onClick={() => {
                              setHighlightedRobotId(task.robot_id)
                              setHighlightedGausiumExecId(task.id)
                              if (task.gausium_map_id != null) {
                                setHighlightedScenarioId(task.gausium_map_id)
                              }
                            }}
                            highlighted={highlightedGausiumExecId === task.id}
                            showCheckbox={true}
                            checked={!hiddenGausiumExecTaskIds.has(task.id)}
                            onCheckboxChange={(checked: boolean) => {
                              setHiddenGausiumExecTaskIds(prev => {
                                const next = new Set(prev)
                                if (checked) next.delete(task.id)
                                else next.add(task.id)
                                return next
                              })
                            }}
                            showLaunchOnlyAction={true}
                            onLaunch={() => {
                              setGausiumLaunchRobotId(typeof task.robot_id === 'number' ? task.robot_id : undefined)
                              setGausiumLaunchMapId(null)
                              setGausiumLaunchExecTask(task as GausiumExecutableTaskRecent)
                            }}
                            scheduleTextOverride={SCHEDULE_PLACEHOLDER_TEXT}
                            displayOffsetMinutes={displayOffsetMinutes}
                          />
                        ))}
                    </div>
                  ) : gausiumExecProgress === PROGRESS_TYPES.WORK ? (
                    <div className={style.emptyWrapper}>
                      <div className={style.emptyTitle}>Loading Gausium tasks...</div>
                    </div>
                  ) : (
                    <div className={style.emptyWrapper}>
                      <div className={style.emptyTitle}>No scheduled cleaning tasks</div>
                      <div className={style.emptyDescription}>
                        Create a scheduled task/Odyssey or wait for Gausium reports in the last 2 weeks.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 1 && (
            <AssetsView
              variant="panel"
              activeTab={assetsSubTab}
              onActiveTabChange={setAssetsSubTab}
              facilityId={facility?.id}
              robots={robots}
              robotsForMap={robots}
              stations={stations}
              stationsForMap={stations}
              lots={lots}
              maintenancePoints={maintenancePoints}
              onRobotsUpdated={onRobotsUpdated}
              onStationsChanged={onStationsChanged}
              onStationsUpdated={onStationsChanged}
              onLiveStationFields={onLiveStationFields}
              onMaintenancePointsChanged={onMaintenancePointsChanged}
              hideDeleteRobots
              onExcludeRobot={async robotToExclude => {
                try {
                  const currentRobotIds: number[] = Array.isArray(facility?.robots_ids)
                    ? facility.robots_ids
                    : []
                  const nextRobotIds = currentRobotIds.filter(id => id !== robotToExclude.id)
                  await MainApi.patchFacility(facility.id, { robots_ids: nextRobotIds })
                  onRobotsUpdated()
                } catch (error) {
                  console.error('Failed to exclude robot from facility:', error)
                }
              }}
              highlightedRobotId={highlightedRobotId}
              onHighlightedRobotIdChange={setHighlightedRobotId}
              highlightedStationId={highlightedStationId}
              onHighlightedStationIdChange={setHighlightedStationId}
              highlightedMaintenancePointId={highlightedMaintenancePointId}
              onHighlightedMaintenancePointIdChange={setHighlightedMaintenancePointId}
              panelRobotsPage={robotsPage}
              panelRobotsPageSize={PAGE_SIZE}
              panelRobotsPageInput={robotsPageInput}
              setPanelRobotsPage={setRobotsPage}
              setPanelRobotsPageInput={setRobotsPageInput}
              onPanelRobotsPageJump={handleRobotsPageJump}
              panelStationsPage={stationsPage}
              panelStationsPageSize={PAGE_SIZE}
              panelStationsPageInput={stationsPageInput}
              setPanelStationsPage={setStationsPage}
              setPanelStationsPageInput={setStationsPageInput}
              onPanelStationsPageJump={handleStationsPageJump}
              panelMaintenancePage={maintenancePage}
              panelMaintenancePageSize={PAGE_SIZE}
              panelMaintenancePageInput={maintenancePageInput}
              setPanelMaintenancePage={setMaintenancePage}
              setPanelMaintenancePageInput={setMaintenancePageInput}
              onPanelMaintenancePageJump={handleMaintenancePageJump}
            />
          )}

          {activeTab === 2 && vendorUiLayout.scenarios.isGausiumMapsMode && (
            <GausiumScenariosTabContent
              gausiumRobotIds={gausiumRobotIds}
              facilityId={facility?.id}
              scenariosPage={scenariosPage}
              scenariosRefreshKey={scenariosRefreshKey}
              onScenarioListRefreshNeeded={() => {
                setScenariosPage(1)
                setScenariosRefreshKey(k => k + 1)
              }}
              onSyncScenarioState={handleGausiumScenariosSync}
              highlightedScenarioId={highlightedScenarioId}
              onScenarioClick={(id, missionIds) => {
                handleScenarioSelect(id)
                setHighlightedMissionIds(missionIds.filter((v): v is number => typeof v === 'number'))
              }}
              scenariosTableClassName={style.scenariosTableRoot}
              hideRobotColumn={isMobile}
              hideIdColumn={isMobile}
            />
          )}

          {activeTab === 2 && vendorUiLayout.scenarios.isGausiumMapsMode && scenariosTotalCount > PAGE_SIZE && (
            <TablePagination
              page={scenariosPage}
              totalPages={Math.ceil(scenariosTotalCount / PAGE_SIZE) || 1}
              pageInput={scenariosPageInput}
              onPageInputChange={setScenariosPageInput}
              onPageInputKeyDown={handleScenariosPageJump}
              onPageInputBlur={() => setScenariosPageInput(scenariosPage.toString())}
              onPrev={() => setScenariosPage(prev => Math.max(prev - 1, 1))}
              onNext={() => setScenariosPage(prev => prev + 1)}
            />
          )}

          {activeTab === 2 && !vendorUiLayout.scenarios.isGausiumMapsMode && (
            <div className={style.missionsTab}>
              {scenariosProgress === PROGRESS_TYPES.WORK && <Loader />}
              {scenariosProgress === PROGRESS_TYPES.ERROR && (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>Error loading scenarios</div>
                </div>
              )}
              {scenariosProgress === PROGRESS_TYPES.SUCCESS && (
                <>
                  <ScenarioTable
                    className={style.scenariosTableRoot}
                    scenarios={scenarios}
                    highlightedScenarioId={highlightedScenarioId}
                    onScenarioClick={(id, missionIds) => {
                      handleScenarioSelect(id)
                      setHighlightedMissionIds(missionIds.filter((v): v is number => typeof v === 'number'))
                    }}
                    onLaunchScenario={scenario => {
                      if (scenario.is_gausium_executable_task && scenario.gausium_exec_task) {
                        setGausiumLaunchRobotId(scenario.gausium_exec_task.robot_id)
                        setGausiumLaunchMapId(null)
                        setGausiumLaunchExecTask(scenario.gausium_exec_task)
                        return
                      }
                      if (scenario.is_gausium_map) {
                        setGausiumLaunchRobotId(undefined)
                        setGausiumLaunchExecTask(null)
                        setGausiumLaunchMapId(scenario.id)
                        return
                      }
                      setLaunchItem({ scenario })
                      setIsLaunchModalOpen(true)
                    }}
                    onScenariosUpdated={() => {
                      setScenariosPage(1)
                      setScenariosRefreshKey(k => k + 1)
                    }}
                    hideRobotColumn={isMobile}
                    hideIdColumn={isMobile}
                    hideDelete={true}
                    showExclude={true}
                  />
                  <TablePagination
                    page={scenariosPage}
                    totalPages={Math.ceil(scenariosTotalCount / PAGE_SIZE) || 1}
                    pageInput={scenariosPageInput}
                    onPageInputChange={setScenariosPageInput}
                    onPageInputKeyDown={handleScenariosPageJump}
                    onPageInputBlur={() => setScenariosPageInput(scenariosPage.toString())}
                    onPrev={() => setScenariosPage(prev => Math.max(prev - 1, 1))}
                    onNext={() => setScenariosPage(prev => prev + 1)}
                  />
                </>
              )}
            </div>
          )}

          {activeTab === 3 && (
            <div className={style.missionsTab}>
              <NormalizedMissionHistoryPanel
                robotSerialNumbers={historyRobotSerialNumbers}
                showRobotColumn={true}
                showStatusColumn={vendorUiLayout.history.showStatusColumn}
                linkScenarioMissionToPages={vendorUiLayout.history.linkScenarioMissionToPages}
                showGausiumReportActions={vendorUiLayout.history.showGausiumReportActions}
                onRowClick={row => {
                  const isGs =
                    row.vendor_origin_record_type === 'gausium_task_report' || String(row.vendor || '') === 'GAUSIUM'
                  if (isGs) {
                    const mapId = parseNumericId(row.scenario_or_map_id)
                    if (mapId != null) {
                      setHighlightedMissionIds([])
                      handleScenarioSelect(mapId)
                    }
                    return
                  }

                  const scenarioId = parseNumericId(row.scenario_or_map_id)
                  if (scenarioId != null) handleScenarioSelect(scenarioId)

                  const originId = parseNumericId(row.vendor_origin_record_id as number | string | null | undefined)
                  const progressIds = (row.missions_progress ?? [])
                    .map(m => parseNumericId((m as { id?: number | string | null })?.id))
                    .filter((id): id is number => id != null && id !== originId)
                  const infoIds = (row.missions_info ?? [])
                    .map(m => parseNumericId(m?.id as number | string | null | undefined))
                    .filter((id): id is number => id != null)
                  const missionIds = progressIds.length > 0 ? progressIds : infoIds
                  if (missionIds.length > 0) {
                    setHighlightedMissionIds(missionIds)
                  } else {
                    const singleMissionId = parseNumericId(row.mission_or_area_id)
                    setHighlightedMissionIds(singleMissionId != null ? [singleMissionId] : [])
                  }
                }}
                displayOffsetMinutes={displayOffsetMinutes}
              />
            </div>
          )}
        </Box>
        {/* Map */}
        <div className={style.mapWrapper} ref={mapWrapperRef}>
          <MapboxCommon
            ref={mapRef}
            disableRuler={false}
            overlayItems={[]}
            onMapInited={onMapInited}
            panelOffset={580}
            onClick={e => {
              if (!e.features || e.features.length === 0) {
                setHighlightedMissionIds([])
              }
            }}
          >
            {activeTab === 0 && (
              <StationsOnMap
                map={map}
                pins={stations
                  .filter(station => station.point)
                  .map(station => ({
                    point: station.point as Point,
                    lots: lots.filter(lot => lot.robot_station === station.id),
                  }))}
              />
            )}
            {(activeTab === 0 || (activeTab === 1 && assetsSubTab === 'maintenance')) && (
              <MaintenancePointsOnMap
                map={map}
                points={maintenancePoints}
                highlightedPointId={highlightedMaintenancePointId}
                onPointClicked={setHighlightedMaintenancePointId}
                interactive={activeTab === 1 && assetsSubTab === 'maintenance'}
              />
            )}
            {activeTab === 1 && assetsSubTab === 'stations' && (
              <AllStationsOnMap
                map={map}
                stations={stations}
                lots={lots}
                onStationClicked={setHighlightedStationId}
                offset={isMobile ? 0 : 540}
              />
            )}
            {activeTab === 0 && coloredScheduledCleaningRoutes.length > 0 && (
              <>
                <CleaningRoutesOnMap
                  cleaningRoutes={coloredScheduledCleaningRoutes}
                  hasSelection={highlightedMissionIds.length > 0}
                />
              </>
            )}
            <AllRobotsOnMap
              map={map}
              robots={
                (activeTab === 1 && assetsSubTab === 'robots') ||
                (activeTab === 0 && vendorUiLayout.isAsbisMinimalProfile)
                  ? robots
                  : []
              }
              onRobotClicked={setHighlightedRobotId}
              offset={isMobile ? 0 : 540}
              interactive={
                (activeTab === 1 && assetsSubTab === 'robots') ||
                (activeTab === 0 && vendorUiLayout.isAsbisMinimalProfile)
              }
              autoFitBounds={activeTab === 1 && assetsSubTab === 'robots'}
            />
            {GAUSIUM_ENABLED && (activeTab === 0 || activeTab === 2 || activeTab === 3) && (
              <GausiumMapGeoPinsOnMap
                map={map}
                pins={gausiumPinsForMap}
                highlightedId={gausiumMapPinHighlightId}
                onPinClick={mapPinId => {
                  const taskRow = scenarios.find(
                    s => s.is_gausium_executable_task && Number(s.gausium_map_pk) === Number(mapPinId)
                  )
                  if (taskRow) handleScenarioSelect(taskRow.id)
                  else handleScenarioSelect(mapPinId)
                }}
              />
            )}
            {(activeTab === 2 || activeTab === 3) && selectedMissionsDetails.length > 0 && (
              <>
                <RestrictZonesOnMap restrictZones={selectedMissionsDetails.flatMap(m => m.restrictZones)} />
                <CleaningRoutesOnMap cleaningRoutes={selectedMissionsDetails.flatMap(m => m.cleaningRoutes)} />
                <TransferRoutesOnMap
                  transferRoutes={selectedMissionsDetails.flatMap(m => [...m.transferRoutes, ...(m.pointRoutes || [])])}
                />
                <CleaningPointsOnMap
                  cleaningRoutes={selectedMissionsDetails.flatMap(m => m.cleaningRoutes)}
                  launchPoints={selectedMissionsDetails.flatMap(m => m.launchPoints)}
                />
                <MissionStartFinishPoints
                  startPoints={selectedMissionsDetails.map(m => m.startingPoint)}
                  finishPoints={selectedMissionsDetails.map(m => m.finishingPoint)}
                />
              </>
            )}
          </MapboxCommon>
          {(activeTab === 2 || activeTab === 3) && (
            <FloorMapPreviewOverlay
              visible={!!selectedGausiumScenarioPreview && !gausiumPreviewDismissed}
              previewUrl={selectedGausiumScenarioPreview?.url}
              mapName={selectedGausiumScenarioPreview?.name}
              mapWidgetRootRef={mapWrapperRef}
              insetLeftPx={SCENARIO_MAP_COLUMN_INSET_LEFT_PX}
              closeOffsetTopPx={SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX}
              onClose={() => setHighlightedScenarioId(undefined)}
            />
          )}
        </div>
        {isMobile && (
          <IconButton
            className={MapTableToggleStyle.toggle}
            onClick={() => setShowSidebar((prev: boolean) => !prev)}
            title={showSidebar ? 'Switch to Map' : 'Switch to Table'}
            style={{
              position: 'absolute',
              bottom: '24px',
              right: '24px',
            }}
          >
            {showSidebar ? (
              <MapIcon className={MapTableToggleStyle.icon} />
            ) : (
              <ListAltIcon className={MapTableToggleStyle.icon} />
            )}
          </IconButton>
        )}
      </div>

      {(() => {
        const isGs = gausiumLaunchMapId != null || gausiumLaunchExecTask != null
        const target: DialogTarget = taskToEdit ? { kind: 'task_all', taskId: taskToEdit.id } : { kind: 'new' }
        const preselectedData = (() => {
          if (gausiumLaunchExecTask != null) {
            return {
              gausiumExecutableTask: gausiumLaunchExecTask,
              robotId: gausiumLaunchRobotId ?? gausiumLaunchExecTask.robot_id,
            }
          }
          if (gausiumLaunchMapId != null) {
            return {
              gausiumMapPk: gausiumLaunchMapId,
              robotId: gausiumLaunchRobotId ?? gausiumRobotIds[0],
            }
          }
          if (taskToEdit) return { task: taskToEdit }
          if (launchItem?.task) return { task: launchItem.task }
          if (launchItem?.scenario) return { scenario: launchItem.scenario }
          if (launchItem?.mission) return { mission: launchItem.mission }
          return undefined
        })()
        const pickerScope: PickerScope = {
          robotIds: robots.map(r => r.id),
          ...(isGs ? { vendor: VENDOR.GAUSIUM } : {}),
        }
        return (
          <TaskSettingsModal
            isOpen={isLaunchModalOpen || gausiumLaunchMapId != null || gausiumLaunchExecTask != null}
            taskName=""
            onClose={() => {
              setIsLaunchModalOpen(false)
              setLaunchItem(undefined)
              setTaskToEdit(undefined)
              setInitialTab(undefined)
              setGausiumLaunchMapId(null)
              setGausiumLaunchExecTask(null)
              setGausiumLaunchRobotId(undefined)
            }}
            onSubmit={() => {
              setIsLaunchModalOpen(false)
              setLaunchItem(undefined)
              setTaskToEdit(undefined)
              setInitialTab(undefined)
              setGausiumLaunchMapId(null)
              setGausiumLaunchExecTask(null)
              setGausiumLaunchRobotId(undefined)
              setScenariosRefreshKey(k => k + 1)
              onRobotsUpdated()
            }}
            target={target}
            preselectedData={preselectedData}
            pickerScope={pickerScope}
            initialTab={initialTab}
            dateToEdit={null}
          />
        )
      })()}

      {openCancelDialog && (
        <ConfirmationDialogAllSingle
          confirmText={t('common.delete')}
          cancelText="Cancel"
          open={openCancelDialog}
          isSmall={false}
          danger={true}
          title={'Cancel task for today?'}
          disableConfirm={
            !deleteReason || (deleteReason === TASK_CANCEL_REASON_OTHER && !deleteDescription)
          }
          submitting={undefined}
          onClose={() => {
            setOpenCancelDialog(false)
            setDeleteReason('')
            setDeleteDescription('')
            setTaskToCancel(null)
          }}
          onSubmit={handleSubmitCancelForToday}
        >
          <MuiBox sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
              Reason
            </Typography>
            <Dropdown
              placeholder="Select Reason"
              currentOption={deleteReason ? { value: deleteReason, text: deleteReason } : undefined}
              options={[...TASK_CANCEL_REASON_OPTIONS]}
              onClickOption={(opt: IOption) => setDeleteReason(String(opt.value))}
              useSmallDropdownStyle={isMobile}
            />
          </MuiBox>
          {deleteReason === TASK_CANCEL_REASON_OTHER && (
            <MuiBox sx={{ mb: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={deleteDescription}
                onChange={e => setDeleteDescription(e.target.value)}
                placeholder="Please provide a reason..."
                required
              />
            </MuiBox>
          )}
        </ConfirmationDialogAllSingle>
      )}

      <AddFacilityModal
        isOpen={isEditFacilityModalOpen}
        onClose={() => setIsEditFacilityModalOpen(false)}
        facilityExisting={facility}
        robots={robots}
        onSave={async data => {
          await MainApi.patchFacility(facility.id, data)
          setIsEditFacilityModalOpen(false)
          onStationsChanged()
          onRobotsUpdated()
        }}
      />
      <SelectMissionModal
        isOpen={isSelectScenarioModalOpen}
        onClose={() => setIsSelectScenarioModalOpen(false)}
        onConfirmClick={handleScenarioSelected}
        robotType={undefined}
        forceScenariosOnly={true}
        allowEmptyScenarios={true}
      />
      {facility?.id != null && (
        <AddMaintenancePointModal
          isOpen={addingNewMaintenancePoint}
          facilityId={facility.id}
          points={maintenancePoints}
          onClose={() => setAddingNewMaintenancePoint(false)}
          onSave={() => {
            onMaintenancePointsChanged()
            setAddingNewMaintenancePoint(false)
          }}
        />
      )}
    </div>
  )
}

export default FacilityView
